// Shrines and the XP Charm: the two ways bench creatures earn XP while the lead fights.
//
// Shrines are rare, rolled and tradeable, the market's big-ticket item. Each has
// posts; a team creature on a post gets a share of the lead's kill XP on top
// (the lead keeps 100%). Made from 10 Shrine Shards (Hellas drops) with a rolled
// tier, or by fusing 5 of one tier into the next, up to Temple. Sanctum and
// Pantheon come only from the roll.
//
// The XP Charm is the cheap, account-bound option: bought with diamonds, held
// by any creature (team or storage), worth a small flat share.

export interface ShrineTier { name: string; share: number; posts: number; weight: number; fusable: boolean }

/** Roll odds and shares follow the reference game's houses (74.5 / 20 / 5 / 0.5 / 0.005). */
export const SHRINE_TIERS: ShrineTier[] = [
  { name: 'Wayside Shrine', share: 0.25, posts: 1, weight: 74.5, fusable: true },
  { name: 'Grove Shrine', share: 0.40, posts: 1, weight: 20, fusable: true },
  { name: 'Temple', share: 0.75, posts: 1, weight: 5, fusable: false },
  { name: 'Sanctum', share: 1.00, posts: 1, weight: 0.5, fusable: false },
  { name: 'Pantheon', share: 1.00, posts: 2, weight: 0.005, fusable: false },
];

export interface Shrine {
  id: string;
  tier: number;
  /** Creature id on each post (0 = empty). Length = the tier's posts. */
  posts: number[];
  /** Counts toward the shrines in use (at most SHRINES_IN_USE). */
  active: boolean;
  /** Made by an admin command: never tradable. */
  dev?: boolean;
}

export const SHRINES_IN_USE = 5;
export const SHARDS_PER_SHRINE = 10;
export const SHRINES_PER_FUSE = 5;
/** XP Charm: share of the lead's kill XP for the holder, wherever it is. Not stacked with a shrine post. */
export const CHARM_SHARE = 0.05;

/** Roll a tier from a uniform draw in [0, 1). */
export function rollShrineTier(u: number): number {
  const total = SHRINE_TIERS.reduce((a, t) => a + t.weight, 0);
  let x = u * total;
  for (let i = 0; i < SHRINE_TIERS.length; i++) { x -= SHRINE_TIERS[i].weight; if (x < 0) return i; }
  return 0;
}

export function shrineChance(tier: number): number {
  return SHRINE_TIERS[tier].weight / SHRINE_TIERS.reduce((a, t) => a + t.weight, 0);
}

export function shrineDesc(s: Pick<Shrine, 'tier'>): string {
  const t = SHRINE_TIERS[s.tier];
  return `${t.posts > 1 ? `${t.posts} slots × ` : ''}${Math.round(t.share * 100)}% XP share`;
}

/**
 * Bench XP per kill: each creature on a post of a shrine in use (team only, not
 * the lead) gets that shrine's share; a charm holder gets the charm share; a
 * creature holding both gets the larger. Returns creature id -> share.
 */
export function benchShares(shrines: Shrine[], charmed: number[], team: number[], leadId: number): Map<number, number> {
  const out = new Map<number, number>();
  const onTeam = new Set(team);
  for (const s of shrines) {
    if (!s.active) continue;
    for (const id of s.posts) {
      if (!id || id === leadId || !onTeam.has(id)) continue;
      out.set(id, Math.max(out.get(id) ?? 0, SHRINE_TIERS[s.tier].share));
    }
  }
  for (const id of charmed) if (id !== leadId) out.set(id, Math.max(out.get(id) ?? 0, CHARM_SHARE));
  return out;
}
